<script setup>
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MultiChannelCampaignsAPI from 'dashboard/api/multiChannelCampaigns';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const RECIPIENTS_PER_PAGE = 50;
const POLL_INTERVAL = 3000;
// Statuses the run moves through on its own — keep polling while we're in one.
const LIVE_RUN_STATUSES = ['planning', 'dispatching'];

const RECIPIENT_FILTERS = [
  { value: '', key: 'ALL' },
  { value: 'sent', key: 'SENT' },
  { value: 'pending', key: 'PENDING' },
  { value: 'failed', key: 'FAILED' },
  { value: 'suppressed', key: 'SUPPRESSED' },
];

const STATUS_TONE = {
  sent: 'text-n-teal-11 bg-n-teal-3',
  pending: 'text-n-slate-11 bg-n-alpha-2',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
  suppressed: 'text-n-amber-11 bg-n-amber-3',
  skipped: 'text-n-slate-11 bg-n-alpha-2',
};

const STATUS_ICON = {
  sent: 'i-lucide-check-check',
  pending: 'i-lucide-clock',
  failed: 'i-lucide-circle-x',
  suppressed: 'i-lucide-filter',
  skipped: 'i-lucide-minus',
};

// Operators think in "Telegram on our number", not in class names.
const CHANNEL_TYPE_KEYS = {
  'Channel::TelegramUser': 'TELEGRAM_USER',
  'Channel::Telegram': 'TELEGRAM_BOT',
  'Channel::Max': 'MAX_USER',
  'Channel::MaxBot': 'MAX_BOT',
  'Channel::Vk': 'VK',
  'Channel::WebWidget': 'WEB_WIDGET',
  'Channel::Email': 'EMAIL',
  'Channel::Whatsapp': 'WHATSAPP',
  'Channel::TwilioSms': 'SMS',
  'Channel::Sms': 'SMS',
};

// Which step of the run the operator is on, for the 1-2-3 header.
const RUN_STEPS = ['LIST', 'CONFIRM', 'SENDING'];

const accountId = computed(() => route.params.accountId);
const campaignId = computed(() => route.params.campaignId);

const campaign = ref(null);
const runs = ref([]);
const activeRun = ref(null);
const recipients = ref([]);
const recipientsCount = ref(0);
const recipientsPage = ref(1);
const recipientStatus = ref('');

const isLoading = ref(true);
const isStarting = ref(false);
const isApproving = ref(false);
const isTogglingPause = ref(false);

let pollTimer = null;

const statusCounts = computed(() => activeRun.value?.stats?.by_status ?? {});
// Rows without an inbox are the unreachable contacts — they're already explained
// under "why nothing was sent", so don't repeat them as a channel.
const channelRows = computed(() =>
  (activeRun.value?.stats?.by_channel ?? []).filter(row => row.inbox_id)
);
const suppressedRows = computed(() =>
  Object.entries(activeRun.value?.stats?.by_suppressed_reason ?? {}).sort(
    (a, b) => b[1] - a[1]
  )
);

const plannedTotal = computed(() => activeRun.value?.recipients_count ?? 0);
const plannedToSend = computed(() => statusCounts.value.pending ?? 0);
const plannedSuppressed = computed(() => statusCounts.value.suppressed ?? 0);

const settled = computed(
  () =>
    (statusCounts.value.sent ?? 0) +
    (statusCounts.value.failed ?? 0) +
    (statusCounts.value.skipped ?? 0)
);
const dispatchTotal = computed(() => settled.value + plannedToSend.value);
const progressPercent = computed(() =>
  dispatchTotal.value
    ? Math.round((settled.value / dispatchTotal.value) * 100)
    : 0
);

const isLiveRun = computed(() =>
  LIVE_RUN_STATUSES.includes(activeRun.value?.status)
);
const hasRunInFlight = computed(() =>
  runs.value.some(run =>
    ['planning', 'awaiting_approval', 'dispatching'].includes(run.status)
  )
);
const isPaused = computed(() => campaign.value?.status === 'paused');

const formatDateTime = ts => (ts ? new Date(ts * 1000).toLocaleString() : '—');

const sendWindow = computed(() => {
  const c = campaign.value;
  if (!c?.send_from || !c?.send_to) {
    return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.WINDOW_ANY');
  }
  return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.WINDOW_VALUE', {
    from: c.send_from,
    to: c.send_to,
    timezone: c.send_timezone,
  });
});

// Mirrors Campaigns::WindowGuard — outside the window the jobs reschedule
// themselves, so recipients sit in "queued" and the run looks stuck otherwise.
const isOutsideWindow = computed(() => {
  const c = campaign.value;
  if (!c?.send_from || !c?.send_to) return false;

  const zoned = new Date(
    new Date().toLocaleString('en-US', { timeZone: c.send_timezone || 'UTC' })
  );
  // send_days is a weekday bitmask (bit = day index, 0 = Sunday).
  const dayEnabled =
    Math.floor((c.send_days ?? 127) / 2 ** zoned.getDay()) % 2 === 1;
  const hours = String(zoned.getHours()).padStart(2, '0');
  const minutes = String(zoned.getMinutes()).padStart(2, '0');
  const now = `${hours}:${minutes}`;

  return !(dayEnabled && now >= c.send_from && now <= c.send_to);
});

const windowClosedNotice = computed(() =>
  t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.WINDOW_CLOSED', {
    from: campaign.value?.send_from,
    timezone: campaign.value?.send_timezone,
  })
);

const inboxTypeLabel = inboxId => {
  const inbox = campaign.value?.inboxes?.find(item => item.id === inboxId);
  const key = CHANNEL_TYPE_KEYS[inbox?.channel_type];
  return key ? t(`CAMPAIGN.MULTI_CHANNEL.DETAIL.CHANNEL_TYPE.${key}`) : '';
};

// Step 1 is done as soon as the list exists; step 3 starts at dispatch.
const currentStep = computed(() => {
  if (activeRun.value?.status === 'planning') return 0;
  if (activeRun.value?.status === 'awaiting_approval') return 1;
  return 2;
});

// Sends are spaced by a random jitter, so the run takes real wall-clock time —
// operators need to know it's ~20 minutes, not instant.
const estimatedDuration = computed(() => {
  const c = campaign.value;
  if (!c || plannedToSend.value === 0) return '';

  const avgMs = ((c.jitter_min_ms ?? 1000) + (c.jitter_max_ms ?? 4000)) / 2;
  const minutes = Math.max(
    1,
    Math.round((plannedToSend.value * avgMs) / 60000)
  );
  if (minutes < 60) {
    return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.DURATION.MINUTES', {
      count: minutes,
    });
  }
  return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.DURATION.HOURS', {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  });
});

// The whole plan in one sentence: how many, through what, how long.
const planSummary = computed(() => {
  const names = channelRows.value
    .map(row => row.inbox_name)
    .filter(Boolean)
    .join(', ');

  return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.SUMMARY', {
    count: plannedToSend.value,
    channels: names,
    duration: estimatedDuration.value,
  });
});

const routingLabel = computed(() => {
  const map = {
    most_responsive: 'MOST_RESPONSIVE',
    priority_order: 'PRIORITY',
    broadcast_all: 'BROADCAST',
  };
  const key = map[campaign.value?.routing_strategy];
  return key
    ? t(`CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.ROUTING.OPTIONS.${key}`)
    : campaign.value?.routing_strategy;
});

const statusLabel = status =>
  t(`CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENT_STATUS.${status.toUpperCase()}`);

const reasonLabel = reason =>
  reason
    ? t(`CAMPAIGN.MULTI_CHANNEL.DETAIL.REASON.${reason.toUpperCase()}`)
    : '';

const chosenReasonLabel = reason =>
  reason && reason !== 'no_reachable_channel'
    ? t(`CAMPAIGN.MULTI_CHANNEL.DETAIL.CHOSEN_REASON.${reason.toUpperCase()}`)
    : '';

// Never leave a row saying just "—": every status gets a reason in plain words.
const recipientDetail = recipient => {
  if (recipient.status === 'sent') return formatDateTime(recipient.sent_at);
  if (recipient.status === 'failed') return recipient.error_message;
  if (recipient.suppressed_reason) {
    return reasonLabel(recipient.suppressed_reason);
  }
  if (recipient.status === 'pending') {
    return t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.WAITING');
  }
  return '';
};

// Surface what the server actually said instead of a blanket "went wrong".
const apiErrorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  t('CAMPAIGN.MULTI_CHANNEL.DETAIL.API.ERROR');

const filterCount = filter =>
  filter.value ? (statusCounts.value[filter.value] ?? 0) : plannedTotal.value;

// Inline "Label: value" pairs — keeps the colon out of the template markup.
const labelWithColon = key => `${t(key)}:`;

const conversationUrl = conversationId =>
  frontendURL(`accounts/${accountId.value}/conversations/${conversationId}`);

const fetchCampaign = async () => {
  const { data } = await MultiChannelCampaignsAPI.show(campaignId.value);
  campaign.value = data;
};

const fetchRecipients = async () => {
  if (!activeRun.value) return;
  const { data } = await MultiChannelCampaignsAPI.getRecipients(
    campaignId.value,
    activeRun.value.id,
    { page: recipientsPage.value, status: recipientStatus.value || undefined }
  );
  recipients.value = data.payload;
  recipientsCount.value = data.meta.count;
};

const fetchRun = async runId => {
  const { data } = await MultiChannelCampaignsAPI.getRun(
    campaignId.value,
    runId
  );
  activeRun.value = data;
};

const fetchRuns = async ({ keepSelection = false } = {}) => {
  const { data } = await MultiChannelCampaignsAPI.getRuns(campaignId.value);
  runs.value = data;
  const selectedId = keepSelection ? activeRun.value?.id : null;
  const nextId = selectedId ?? data[0]?.id;
  if (nextId) await fetchRun(nextId);
};

const stopPolling = () => {
  clearInterval(pollTimer);
  pollTimer = null;
};

const poll = async () => {
  if (!activeRun.value) return;
  await fetchRun(activeRun.value.id);
  await fetchRecipients();
  if (!isLiveRun.value) {
    stopPolling();
    // The run just settled — campaign status and counters moved with it.
    await Promise.all([fetchCampaign(), fetchRuns({ keepSelection: true })]);
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(poll, POLL_INTERVAL);
};

const selectRun = async runId => {
  recipientsPage.value = 1;
  recipientStatus.value = '';
  await fetchRun(runId);
  await fetchRecipients();
  if (isLiveRun.value) startPolling();
};

const handleStartRun = async () => {
  isStarting.value = true;
  try {
    const { data } = await MultiChannelCampaignsAPI.createRun(campaignId.value);
    recipientsPage.value = 1;
    recipientStatus.value = '';
    activeRun.value = data;
    await fetchRuns({ keepSelection: true });
    startPolling();
  } catch (error) {
    useAlert(apiErrorMessage(error));
  } finally {
    isStarting.value = false;
  }
};

const handleApprove = async () => {
  isApproving.value = true;
  try {
    await MultiChannelCampaignsAPI.approveRun(
      campaignId.value,
      activeRun.value.id
    );
    useAlert(t('CAMPAIGN.MULTI_CHANNEL.DETAIL.API.DISPATCH_STARTED'));
    await fetchRun(activeRun.value.id);
    await fetchCampaign();
    startPolling();
  } catch (error) {
    useAlert(apiErrorMessage(error));
  } finally {
    isApproving.value = false;
  }
};

const handleCancelRun = async () => {
  try {
    await MultiChannelCampaignsAPI.cancelRun(
      campaignId.value,
      activeRun.value.id
    );
    stopPolling();
    await fetchRuns({ keepSelection: true });
    await fetchRecipients();
  } catch (error) {
    useAlert(apiErrorMessage(error));
  }
};

const handleTogglePause = async () => {
  isTogglingPause.value = true;
  try {
    if (isPaused.value) {
      await MultiChannelCampaignsAPI.resume(campaignId.value);
    } else {
      await MultiChannelCampaignsAPI.pause(campaignId.value);
    }
    await fetchCampaign();
  } catch (error) {
    useAlert(apiErrorMessage(error));
  } finally {
    isTogglingPause.value = false;
  }
};

const handleFilterChange = async status => {
  recipientStatus.value = status;
  recipientsPage.value = 1;
  await fetchRecipients();
};

const changePage = async delta => {
  recipientsPage.value += delta;
  await fetchRecipients();
};

const goBack = () =>
  router.push({ name: 'campaigns_multi_channel_index', params: route.params });

const loadAll = async () => {
  isLoading.value = true;
  activeRun.value = null;
  recipients.value = [];
  recipientsPage.value = 1;
  recipientStatus.value = '';
  try {
    await fetchCampaign();
    await fetchRuns();
    await fetchRecipients();
    if (isLiveRun.value) startPolling();
  } catch (error) {
    useAlert(apiErrorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadAll);

// The campaigns router-view keeps this page alive, so a different campaign can
// land on a cached instance — reload when that happens, and never poll offscreen.
onActivated(() => {
  if (String(campaign.value?.id) === String(campaignId.value)) {
    if (isLiveRun.value) startPolling();
    return;
  }
  loadAll();
});

onDeactivated(stopPolling);
onUnmounted(stopPolling);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-3">
          <div class="flex items-center min-w-0 gap-2">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-arrow-left"
              @click="goBack"
            />
            <span class="truncate text-heading-1 text-n-slate-12">
              {{ campaign?.title }}
            </span>
            <!-- While a run is on screen its step header is the state; a stale
                 "Черновик" next to it only contradicts what the operator did. -->
            <span
              v-if="
                campaign &&
                !isLiveRun &&
                activeRun?.status !== 'awaiting_approval'
              "
              class="px-2 py-0.5 text-xs rounded-md shrink-0 bg-n-alpha-3 text-n-slate-11"
            >
              {{
                t(
                  `CAMPAIGN.MULTI_CHANNEL.DETAIL.CAMPAIGN_STATUS.${campaign.status.toUpperCase()}`
                )
              }}
            </span>
          </div>
          <Button
            v-if="campaign"
            :label="
              runs.length
                ? t('CAMPAIGN.MULTI_CHANNEL.DETAIL.START_AGAIN')
                : t('CAMPAIGN.MULTI_CHANNEL.DETAIL.START')
            "
            icon="i-lucide-send"
            size="sm"
            class="shrink-0"
            :is-loading="isStarting"
            :disabled="isStarting || hasRunInFlight"
            @click="handleStartRun"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="flex flex-col w-full max-w-5xl gap-4 py-4 mx-auto">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-16 text-n-slate-11"
        >
          <Spinner />
        </div>

        <template v-else-if="campaign">
          <!-- Campaign summary -->
          <div
            class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
          >
            <p class="m-0 text-sm whitespace-pre-line text-n-slate-12">
              {{ campaign.default_message }}
            </p>
            <div
              class="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-n-slate-11"
            >
              <div class="flex flex-wrap items-center gap-1.5">
                <span>
                  {{
                    labelWithColon(
                      'CAMPAIGN.MULTI_CHANNEL.DETAIL.CHANNELS_LABEL'
                    )
                  }}
                </span>
                <span
                  v-for="inbox in campaign.inboxes"
                  :key="inbox.id"
                  class="px-2 py-0.5 rounded-md bg-n-alpha-3 text-n-slate-12"
                >
                  {{ inbox.name }}
                </span>
                <span v-if="!campaign.inboxes.length" class="text-n-ruby-11">
                  {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.NO_CHANNELS') }}
                </span>
              </div>
              <span>
                {{
                  labelWithColon('CAMPAIGN.MULTI_CHANNEL.DETAIL.ROUTING_LABEL')
                }}
                <span class="text-n-slate-12">{{ routingLabel }}</span>
              </span>
              <span>
                {{
                  labelWithColon('CAMPAIGN.MULTI_CHANNEL.DETAIL.WINDOW_LABEL')
                }}
                <span class="text-n-slate-12">{{ sendWindow }}</span>
              </span>
            </div>
          </div>

          <!-- No runs yet -->
          <div
            v-if="!activeRun"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <div
              class="flex items-center justify-center rounded-2xl size-16 bg-n-alpha-2"
            >
              <Icon icon="i-lucide-send" class="text-n-slate-11 size-7" />
            </div>
            <div class="flex flex-col max-w-md gap-1">
              <span class="text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.EMPTY.TITLE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.EMPTY.SUBTITLE') }}
              </span>
            </div>
          </div>

          <template v-else>
            <!-- Run state -->
            <div
              class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.TITLE', {
                      id: activeRun.id,
                    })
                  }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ formatDateTime(activeRun.triggered_at) }}
                </span>
              </div>

              <!-- Where we are: собрали список → подтвердите → отправляем -->
              <ol
                v-if="activeRun.status !== 'cancelled'"
                class="flex flex-wrap items-center gap-2 p-0 m-0 list-none"
              >
                <li
                  v-for="(step, index) in RUN_STEPS"
                  :key="step"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg"
                  :class="
                    index === currentStep
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-1 text-n-slate-11'
                  "
                >
                  <Icon
                    v-if="index < currentStep"
                    icon="i-lucide-check"
                    class="size-3"
                  />
                  <span v-else class="tabular-nums">{{ index + 1 }}.</span>
                  {{ t(`CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.STEPS.${step}`) }}
                </li>
              </ol>

              <!-- Planning -->
              <div
                v-if="activeRun.status === 'planning'"
                class="flex items-center gap-3 py-2"
              >
                <Spinner class="text-n-brand" />
                <div class="flex flex-col">
                  <span class="text-sm text-n-slate-12">
                    {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLANNING.TITLE') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLANNING.SUBTITLE')
                    }}
                  </span>
                </div>
              </div>

              <!-- Plan preview / progress -->
              <template v-else>
                <div class="grid grid-cols-3 gap-3">
                  <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
                    <span
                      class="text-2xl font-medium tabular-nums text-n-slate-12"
                    >
                      {{ plannedTotal }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.TOTAL') }}
                    </span>
                  </div>
                  <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
                    <span
                      class="text-2xl font-medium tabular-nums text-n-teal-11"
                    >
                      {{
                        activeRun.status === 'awaiting_approval'
                          ? plannedToSend
                          : (statusCounts.sent ?? 0)
                      }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{
                        activeRun.status === 'awaiting_approval'
                          ? t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.TO_SEND')
                          : t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.SENT')
                      }}
                    </span>
                  </div>
                  <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
                    <span
                      class="text-2xl font-medium tabular-nums text-n-amber-11"
                    >
                      {{ plannedSuppressed }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{
                        t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.SUPPRESSED')
                      }}
                    </span>
                  </div>
                </div>

                <!-- Progress bar once sending has started -->
                <div
                  v-if="activeRun.status !== 'awaiting_approval'"
                  class="flex flex-col gap-2"
                >
                  <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
                    <div
                      class="h-full transition-all duration-500 rounded-full bg-n-teal-9"
                      :style="{ width: `${progressPercent}%` }"
                    />
                  </div>
                  <div
                    class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11"
                  >
                    <span>
                      {{
                        labelWithColon(
                          'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.QUEUED'
                        )
                      }}
                      <span class="text-n-slate-12">
                        {{ statusCounts.pending ?? 0 }}
                      </span>
                    </span>
                    <span v-if="statusCounts.failed">
                      {{
                        labelWithColon(
                          'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.FAILED'
                        )
                      }}
                      <span class="text-n-ruby-11">
                        {{ statusCounts.failed }}
                      </span>
                    </span>
                    <span v-if="activeRun.dispatch_completed_at">
                      {{
                        t(
                          'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.FINISHED_AT',
                          {
                            time: formatDateTime(
                              activeRun.dispatch_completed_at
                            ),
                          }
                        )
                      }}
                    </span>
                  </div>
                </div>

                <!-- Channel breakdown -->
                <div v-if="channelRows.length" class="flex flex-col gap-2">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.BY_CHANNEL') }}
                  </span>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="row in channelRows"
                      :key="row.inbox_id"
                      class="inline-flex items-center gap-2 px-2.5 py-1 text-xs rounded-lg bg-n-alpha-1 text-n-slate-12"
                    >
                      {{ row.inbox_name }}
                      <span
                        v-if="inboxTypeLabel(row.inbox_id)"
                        class="text-n-slate-10"
                      >
                        {{ inboxTypeLabel(row.inbox_id) }}
                      </span>
                      <span class="tabular-nums text-n-slate-11">
                        {{ row.total }}
                      </span>
                    </span>
                  </div>
                </div>

                <!-- Why contacts were filtered out -->
                <div v-if="suppressedRows.length" class="flex flex-col gap-2">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{
                      t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.WHY_SKIPPED')
                    }}
                  </span>
                  <div class="flex flex-col gap-1">
                    <div
                      v-for="[reason, count] in suppressedRows"
                      :key="reason"
                      class="flex items-center justify-between px-3 py-1.5 text-xs rounded-lg bg-n-alpha-1"
                    >
                      <span class="text-n-slate-11">{{
                        reasonLabel(reason)
                      }}</span>
                      <span class="tabular-nums text-n-slate-12">{{
                        count
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div
                  v-if="activeRun.status === 'awaiting_approval'"
                  class="flex flex-col gap-2"
                >
                  <p
                    v-if="plannedToSend > 0"
                    class="m-0 text-sm text-n-slate-12"
                  >
                    {{ planSummary }}
                  </p>
                  <div class="flex items-center gap-3">
                    <Button
                      :label="
                        t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.APPROVE')
                      "
                      icon="i-lucide-send"
                      size="sm"
                      :is-loading="isApproving"
                      :disabled="isApproving || plannedToSend === 0"
                      @click="handleApprove"
                    />
                    <Button
                      variant="faded"
                      color="slate"
                      size="sm"
                      :label="
                        t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.CANCEL')
                      "
                      @click="handleCancelRun"
                    />
                  </div>
                  <span
                    v-if="isOutsideWindow && plannedToSend > 0"
                    class="text-xs text-n-amber-11"
                  >
                    {{ windowClosedNotice }}
                  </span>
                  <span
                    v-if="plannedToSend === 0"
                    class="text-xs text-n-amber-11"
                  >
                    {{
                      campaign.inboxes.length
                        ? t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.NOBODY')
                        : t(
                            'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.NO_CHANNELS_HINT'
                          )
                    }}
                  </span>
                </div>

                <div
                  v-else-if="activeRun.status === 'dispatching'"
                  class="flex flex-col gap-2"
                >
                  <div class="flex items-center gap-3">
                    <Button
                      variant="faded"
                      color="slate"
                      size="sm"
                      :icon="isPaused ? 'i-lucide-play' : 'i-lucide-pause'"
                      :label="
                        isPaused
                          ? t(
                              'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.RESUME'
                            )
                          : t(
                              'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.PAUSE'
                            )
                      "
                      :is-loading="isTogglingPause"
                      @click="handleTogglePause"
                    />
                    <Button
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      :label="
                        t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PLAN.CANCEL')
                      "
                      @click="handleCancelRun"
                    />
                  </div>
                  <span v-if="isPaused" class="text-xs text-n-amber-11">
                    {{
                      t(
                        'CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.PAUSED_HINT'
                      )
                    }}
                  </span>
                  <span
                    v-else-if="isOutsideWindow"
                    class="text-xs text-n-amber-11"
                  >
                    {{ windowClosedNotice }}
                  </span>
                </div>

                <span
                  v-else-if="activeRun.status === 'cancelled'"
                  class="text-xs text-n-slate-11"
                >
                  {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.CANCELLED') }}
                </span>
              </template>
            </div>

            <!-- Recipients -->
            <div class="flex flex-col gap-3">
              <div class="flex flex-wrap items-center gap-2">
                <span class="mr-1 text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.TITLE') }}
                </span>
                <button
                  v-for="filter in RECIPIENT_FILTERS"
                  :key="filter.key"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg transition-colors"
                  :class="
                    recipientStatus === filter.value
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                  "
                  @click="handleFilterChange(filter.value)"
                >
                  {{
                    t(
                      `CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.FILTER.${filter.key}`
                    )
                  }}
                  <span class="tabular-nums opacity-70">
                    {{ filterCount(filter) }}
                  </span>
                </button>
              </div>

              <div class="overflow-hidden border rounded-xl border-n-weak">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-xs text-left text-n-slate-11 bg-n-alpha-2">
                      <th class="px-4 py-2 font-medium">
                        {{
                          t(
                            'CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.COLUMN.CONTACT'
                          )
                        }}
                      </th>
                      <th class="px-4 py-2 font-medium">
                        {{
                          t(
                            'CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.COLUMN.CHANNEL'
                          )
                        }}
                      </th>
                      <th class="px-4 py-2 font-medium">
                        {{
                          t(
                            'CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.COLUMN.STATUS'
                          )
                        }}
                      </th>
                      <th class="px-4 py-2 font-medium">
                        {{
                          t(
                            'CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.COLUMN.DETAIL'
                          )
                        }}
                      </th>
                      <th class="w-10 px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="recipient in recipients"
                      :key="recipient.id"
                      class="border-t border-n-weak hover:bg-n-alpha-1"
                    >
                      <td class="px-4 py-2 text-n-slate-12">
                        {{
                          recipient.contact_name || `#${recipient.contact_id}`
                        }}
                      </td>
                      <td class="px-4 py-2 text-n-slate-11">
                        <div class="flex flex-col">
                          <span>{{ recipient.chosen_inbox_name || '—' }}</span>
                          <span
                            v-if="chosenReasonLabel(recipient.chosen_reason)"
                            class="text-xs text-n-slate-10"
                          >
                            {{ chosenReasonLabel(recipient.chosen_reason) }}
                          </span>
                        </div>
                      </td>
                      <td class="px-4 py-2">
                        <span
                          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md"
                          :class="STATUS_TONE[recipient.status]"
                        >
                          <Icon
                            :icon="STATUS_ICON[recipient.status]"
                            class="size-3"
                          />
                          {{ statusLabel(recipient.status) }}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-xs text-n-slate-11">
                        <span
                          :class="{
                            'text-n-ruby-11': recipient.status === 'failed',
                          }"
                        >
                          {{ recipientDetail(recipient) }}
                        </span>
                      </td>
                      <td class="px-4 py-2">
                        <router-link
                          v-if="recipient.conversation_id"
                          :to="conversationUrl(recipient.conversation_id)"
                          class="text-n-slate-10 hover:text-n-brand"
                          :title="
                            t(
                              'CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.OPEN_CONVERSATION'
                            )
                          "
                        >
                          <Icon icon="i-lucide-external-link" class="size-4" />
                        </router-link>
                      </td>
                    </tr>
                    <tr v-if="!recipients.length">
                      <td
                        colspan="5"
                        class="px-4 py-8 text-sm text-center text-n-slate-11"
                      >
                        {{
                          t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.EMPTY')
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="recipientsCount > RECIPIENTS_PER_PAGE"
                class="flex items-center justify-end gap-3 text-xs text-n-slate-11"
              >
                <span>
                  {{
                    t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RECIPIENTS.PAGINATION', {
                      from: (recipientsPage - 1) * RECIPIENTS_PER_PAGE + 1,
                      to: Math.min(
                        recipientsPage * RECIPIENTS_PER_PAGE,
                        recipientsCount
                      ),
                      count: recipientsCount,
                    })
                  }}
                </span>
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  icon="i-lucide-chevron-left"
                  :disabled="recipientsPage === 1"
                  @click="changePage(-1)"
                />
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  icon="i-lucide-chevron-right"
                  :disabled="
                    recipientsPage * RECIPIENTS_PER_PAGE >= recipientsCount
                  "
                  @click="changePage(1)"
                />
              </div>
            </div>

            <!-- Run history -->
            <div v-if="runs.length > 1" class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.HISTORY') }}
              </span>
              <button
                v-for="run in runs"
                :key="run.id"
                type="button"
                class="flex items-center justify-between gap-3 px-4 py-2 text-xs transition-colors border rounded-lg border-n-weak"
                :class="
                  run.id === activeRun.id
                    ? 'bg-n-alpha-3 text-n-slate-12'
                    : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="selectRun(run.id)"
              >
                <span>
                  {{
                    t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.TITLE', { id: run.id })
                  }}
                  · {{ formatDateTime(run.triggered_at) }}
                </span>
                <span class="flex items-center gap-3 tabular-nums">
                  <span>
                    {{ t('CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN.PROGRESS.SENT') }}
                    {{ run.sent_count }}/{{ run.recipients_count }}
                  </span>
                  <span class="px-2 py-0.5 rounded-md bg-n-alpha-2">
                    {{
                      t(
                        `CAMPAIGN.MULTI_CHANNEL.DETAIL.RUN_STATUS.${run.status.toUpperCase()}`
                      )
                    }}
                  </span>
                </span>
              </button>
            </div>
          </template>
        </template>
      </div>
    </main>
  </section>
</template>
